import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { fetchPatientById, fetchRecommendationById, fetchStudyChartFacts, fetchWithAuth } from '../../api';
import { auth } from '../../firebase';
import {
  buildAllianceOutline,
  eligibleEnrollmentValues,
  loadAllianceFields,
  localToday,
  blankProcedureRow,
  imagingFollowUpValues,
  procedureFollowUpValues,
  spineHistoryValues,
  syncImagingFollowUps,
  syncIndexYesFollowUps,
  syncTouchedProcedureFollowUps,
  syncAdverseEventFollowUps,
  syncBaselineDerived,
  syncBaselineFollowUps,
  syncHistoryTherapies,
  syncSpineHistory,
  syncVisitFollowUps,
  VISIT_TIMEPOINT,
  withVisitTimepoint,
  DEFAULT_SURGEON,
  DEFAULT_SURGEON_ORG,
  adverseEventValues,
  historyTherapyValues,
  applyBaselineTestDate,
  baselineDerivedValues,
  baselineFollowUpValues,
  isBaselineTestDate,
  visitFollowUpValues,
  yesNoFollowUpValues,
  withSubjectCasebook,
} from '../../alliance/allianceModel';
import { prefillAllianceChart } from '../../alliance/prefillFromChart';
import { applyJointDecisionsHandoff } from '../../studies/prefillFromJointDecisions';
import { stampPatientDemographics } from '../../studies/applyChartFacts';
import {
  completedSubsectionKeys,
  findLocation,
  firstLocation,
  formProgress,
  instanceKey,
  outlineStops,
  applySharedFormDate,
  calendarDate,
  isFirstCalendarAnswer,
  visibleFieldEntries,
} from '../../compass/compassModel';
import { phaseFlagsForOutline } from '../../studies/studyPipeline';
import {
  adoptStudy,
  broadcastStudy,
  clearExampleChart,
  commitLinkedEdit,
  demoChartsUseDatabase,
  flushDemoChart,
  hydrateDemoChart,
  loadStudy,
  overlaySavedAnswers,
  patientChartId,
  persistDemoStudy,
  saveStudy,
  savedDemographicsBlank,
  studyWasOpened,
} from '../../studies/studyChartStore';
import CompassForm, { FormNavbar } from '../compass/CompassForm';
import { useWhatsNext } from '../compass/useWhatsNext';
import LoadingSpinner from '../LoadingSpinner';
import '../compass/compass.css';

function emptyState() {
  return withSubjectCasebook({ active: null, forms: {} });
}

function savedState(demo, patientId) {
  const saved = loadStudy(patientChartId(demo, patientId), 'alliance');
  if (!saved || typeof saved !== 'object') return null;
  return { ...emptyState(), ...saved, forms: saved.forms || {} };
}

function formValues(state, key) {
  return state.forms[key]?.values || {};
}

export default function AllianceWorkspace({
  demo = false,
  patient = null,
  embedded = false,
  onEngage,
  handoff = null,
}) {
  const [fields, setFields] = useState(null);
  const [error, setError] = useState('');
  const [state, setState] = useState(() => (demo ? emptyState() : (savedState(false, null) || emptyState())));
  const [revealed, setRevealed] = useState({});
  const [filledFromChart, setFilledFromChart] = useState(false);
  const [chartReady, setChartReady] = useState(() => !demo);
  const [chartPatientId, setChartPatientId] = useState(patient?.id ?? null);
  if ((patient?.id ?? null) !== chartPatientId) {
    setChartPatientId(patient?.id ?? null);
    setChartReady(!demo && !patient?.id);
  }
  const [dateOffer, setDateOffer] = useState(null);
  const [defaultSurgeon, setDefaultSurgeon] = useState(false);
  const dateDeclinedRef = useRef({ baseline: false, consent: false });
  const stateRef = useRef(state);
  const ownerRef = useRef(null);
  const chartIdRef = useRef('');
  const handoffRef = useRef(handoff);
  const patientRef = useRef(patient);
  const appliedHandoffRef = useRef('');
  stateRef.current = state;
  chartIdRef.current = patientChartId(demo, patient?.id);
  handoffRef.current = handoff;
  patientRef.current = patient;
  const handoffKey = handoff
    ? `${handoff.procedureKey || ''}|${handoff.surgeryDate || ''}|${(handoff.studies || []).map((study) => study.id).join(',')}`
    : '';

  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.PUBLIC_URL || ''}/medtronic_alliance_questions.csv`;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('The Alliance field list did not load.');
        return response.text();
      })
      .then((text) => {
        if (!cancelled) setFields(loadAllianceFields(text));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || 'The Alliance field list did not load.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setRevealed({});
  }, [patient?.id]);

  useEffect(() => {
    const marker = `${patient?.id || ''}|${handoffKey}`;
    const focusConsent = Boolean(handoffKey) && appliedHandoffRef.current !== marker;
    if (handoffKey) appliedHandoffRef.current = marker;
    const publish = (chartId, nextState) => {
      const consent = (handoffRef.current?.studies || []).map((study) => study.consent).find(Boolean);
      const active = stateRef.current?.active;
      const userLeftConsent = Boolean(
        consent
        && active?.formId
        && (active.formId !== consent.formId || (active.phaseId || '') !== (consent.phaseId || '')),
      );
      const focus = focusConsent && !userLeftConsent;
      const base = nextState || emptyState();
      const withPlace = !focus && active?.formId
        ? { ...base, active }
        : base;
      const adopted = chartId ? adoptStudy(chartId, 'alliance', withPlace) : withPlace;
      const next = withSubjectCasebook(
        applyJointDecisionsHandoff(adopted, handoffRef.current, { focusConsent: focus }),
      );
      if (chartId && handoffRef.current) return broadcastStudy(chartId, 'alliance', next);
      return next;
    };

    if (!demo) {
      ownerRef.current = null;
      setChartReady(true);
      setState(publish('example', savedState(false, null) || emptyState()));
      return undefined;
    }
    const id = patientRef.current?.id;
    if (ownerRef.current && ownerRef.current !== id) {
      saveStudy(patientChartId(true, ownerRef.current), 'alliance', stateRef.current);
      flushDemoChart(ownerRef.current);
    }
    if (!id) {
      ownerRef.current = null;
      setChartReady(true);
      setState(emptyState());
      return undefined;
    }

    let cancel = false;
    ownerRef.current = id;
    setChartReady(false);
    (async () => {
      try {
      try {
        await hydrateDemoChart(id);
      } catch (loadError) {
        // The form still opens from Phoenix when the saved chart cannot be read.
      }
      if (cancel || ownerRef.current !== id) return;
    const chartId = patientChartId(true, id);
    const saved = savedState(true, id);
    const seeded = stampPatientDemographics(saved || emptyState(), patientRef.current, 'alliance');
    const ethnicityValues = seeded.forms?.['ethnicity::case']?.values || {};
    const ethnicity = ethnicityValues['alliance.ethnicity#0'] || ethnicityValues['alliance.ethnicity'];
    if (
      saved
      && studyWasOpened(chartId, 'alliance')
      && ethnicity
      && !savedDemographicsBlank(seeded, 'alliance')
      && !handoffRef.current
    ) {
      setFilledFromChart(true);
      setState(publish(chartId, seeded));
      setChartReady(true);
      return;
    }

    setFilledFromChart(Boolean(patientRef.current?.phi) || Boolean(handoffRef.current));
    setState(publish(chartId, seeded));
      let current = patientRef.current;
      try {
        const loaded = await fetchPatientById(id);
        if (loaded && typeof loaded === 'object') current = { ...patientRef.current, ...loaded };
      } catch (loadError) {
        current = patientRef.current;
      }
      let chartFacts = null;
      let recommendation = null;
      try {
        chartFacts = await fetchStudyChartFacts(id, current?.surgery_start_time);
      } catch (loadError) {
        chartFacts = null;
      }
      if (current?.primary_recommendation_id) {
        try {
          recommendation = await fetchRecommendationById(id, current.primary_recommendation_id);
        } catch (loadError) {
          recommendation = null;
        }
      }
      if (cancel || ownerRef.current !== id) return;
      let filled = seeded;
      try {
        filled = stampPatientDemographics(
          prefillAllianceChart({ patient: current, chartFacts, recommendation }),
          current,
          'alliance',
        );
      } catch (loadError) {
        filled = stampPatientDemographics(seeded, current, 'alliance');
      }
      setFilledFromChart(true);
      setState(publish(chartId, overlaySavedAnswers(filled, savedState(true, id) || saved)));
      setChartReady(true);
    } catch (loadError) {
      if (!cancel && ownerRef.current === id) setChartReady(true);
    }
    })();
    return () => {
      cancel = true;
      flushDemoChart(id);
    };
  }, [demo, handoffKey, patient?.id, patient?.surgery_start_time, patient?.primary_recommendation_id]);

  const outline = useMemo(
    () => (fields ? buildAllianceOutline(fields) : []),
    [fields],
  );

  useEffect(() => {
    if (!demo || !patient?.id || !chartReady) return undefined;
    const current = stateRef.current;
    const ready = outline.length > 0;
    const next = ready
      ? { ...current, phases: phaseFlagsForOutline(outline, current.forms) }
      : current;
    persistDemoStudy(
      patient.id,
      'alliance',
      next,
      ready ? completedSubsectionKeys(outline, next.forms) : null,
    );
    return undefined;
  }, [demo, patient?.id, state, outline, chartReady]);

  const location = findLocation(outline, state.active) || (outline.length ? (() => {
    const next = firstLocation(outline);
    return findLocation(outline, next);
  })() : null);

  useEffect(() => {
    if (!outline.length) return;
    setState((current) => (
      findLocation(outline, current.active)
        ? current
        : { ...current, active: firstLocation(outline) }
    ));
  }, [outline]);

  const formsForNext = () => outlineStops(outline).map((stop) => {
    const formKey = instanceKey(stop.form.id, stop.visitId);
    const saved = state.forms[formKey] || {};
    return {
      formKey,
      fields: visibleFieldEntries(stop.form.fields, saved.values || {}, saved.repeats || {}),
    };
  });

  const { select, goToNextMissing, toggleLock, focusFieldKey, following, locked } = useWhatsNext({
    outline,
    state,
    setState,
    formsFor: formsForNext,
    resumeKey: chartReady && outline.length ? (patient?.id || '') : '',
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const profile = await fetchWithAuth(`api/user/${user.uid}`);
        if (!cancel && profile?.organization_id === DEFAULT_SURGEON_ORG) setDefaultSurgeon(true);
      } catch (error) {
        // The surgeon name stays blank when the profile cannot be read.
      }
    })();
    return () => {
      cancel = true;
    };
  }, [patient?.id]);

  const activeKey = state.active ? instanceKey(state.active.formId, state.active.visitId) : '';

  useEffect(() => {
    if (!chartReady || !outline.length) return;
    setState((current) => {
      const forms = { ...(current.forms || {}) };
      let changed = false;
      outlineStops(outline).forEach((stop) => {
        const formKey = instanceKey(stop.form.id, stop.visitId);
        const needsTimepoint = Boolean(VISIT_TIMEPOINT[stop.visitId]) && formKey === activeKey;
        const needsSurgeon = defaultSurgeon && stop.form.id === 'operative';
        const form = forms[formKey];
        if (!form && !needsTimepoint && !needsSurgeon) return;
        const saved = form || { values: {}, repeats: {} };
        let nextValues = saved.values || {};
        if (formKey === 'operative::case') {
          nextValues = syncIndexYesFollowUps(syncImagingFollowUps(syncTouchedProcedureFollowUps(nextValues)));
        }
        nextValues = syncHistoryTherapies(syncAdverseEventFollowUps(syncSpineHistory(
          stop.form.fields,
          syncBaselineDerived(syncBaselineFollowUps(stop.form.fields, syncVisitFollowUps(stop.form.fields, nextValues))),
          saved.repeats,
        )));
        nextValues = withVisitTimepoint(nextValues, stop.visitId);
        if (needsSurgeon && !String(nextValues['alliance.index.surgeon'] || '').trim()) {
          nextValues = { ...nextValues, 'alliance.index.surgeon': DEFAULT_SURGEON };
        }
        const before = saved.values || {};
        const keys = new Set([...Object.keys(before), ...Object.keys(nextValues)]);
        if (form && [...keys].every((item) => before[item] === nextValues[item])) return;
        forms[formKey] = { ...saved, values: nextValues };
        changed = true;
      });
      if (!changed) return current;
      return { ...current, forms };
    });
  }, [activeKey, chartReady, defaultSurgeon, outline, patient?.id]);

  const updateValue = (key, fieldKey, value) => {
    let offer = null;
    setState((current) => {
      const form = current.forms[key] || { values: {}, repeats: {} };
      let nextValues = yesNoFollowUpValues(
        imagingFollowUpValues(procedureFollowUpValues(form.values, fieldKey, value), fieldKey, value),
        fieldKey,
        value,
      );
      const stop = outlineStops(outline).find((item) => instanceKey(item.form.id, item.visitId) === key);
      nextValues = baselineDerivedValues(
        baselineFollowUpValues(stop?.form.fields, visitFollowUpValues(stop?.form.fields, nextValues, fieldKey, value), fieldKey, value),
        fieldKey,
        value,
      );
      if (fieldKey === 'alliance.spine_history.any') {
        if (stop) nextValues = spineHistoryValues(stop.form.fields, form.values, form.repeats || {}, value);
      }
      nextValues = historyTherapyValues(nextValues, fieldKey, value);
      nextValues = adverseEventValues(nextValues, fieldKey, value);
      const prior = form.values || {};
      const repeats = form.repeats || {};
      const consentStops = outlineStops(outline).filter((item) => (
        item.form.id === 'econsent-status' || item.form.id === 'econsent-document' || item.form.id === 'consent'
      ));
      const firstBaseline = stop?.form.id === 'baseline'
        && isBaselineTestDate(fieldKey)
        && /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))
        && !(stop?.form.fields || []).some((field) => (
          field.field_key !== fieldKey
          && isBaselineTestDate(field.field_key)
          && prior[field.field_key]
          && prior[field.field_key] !== 'N/A'
        ));
      const firstConsent = consentStops.some((item) => instanceKey(item.form.id, item.visitId) === key)
        && isFirstCalendarAnswer(fieldKey, value, consentStops.map((item) => {
          const formKey = instanceKey(item.form.id, item.visitId);
          return {
            fields: item.form.fields,
            values: formKey === key ? prior : (current.forms[formKey]?.values || {}),
            repeats: formKey === key ? repeats : (current.forms[formKey]?.repeats || {}),
          };
        }));
      if (firstBaseline && !dateDeclinedRef.current.baseline) offer = { formKey: key, date: value, kind: 'baseline' };
      if (firstConsent && !dateDeclinedRef.current.consent) offer = { formKey: key, date: value, kind: 'consent' };
      const edited = {
        ...current,
        forms: {
          ...current.forms,
          [key]: { ...form, values: nextValues },
        },
      };
      return commitLinkedEdit(chartIdRef.current, 'alliance', edited, fieldKey, key);
    });
    if (offer) setDateOffer(offer);
  };

  const applyOfferedDate = () => {
    if (!dateOffer) return;
    const offer = dateOffer;
    setDateOffer(null);
    dateDeclinedRef.current[offer.kind] = true;
    setState((current) => {
      if (offer.kind === 'consent') {
        const forms = { ...current.forms };
        outlineStops(outline).forEach((stop) => {
          if (!['econsent-status', 'econsent-document', 'consent'].includes(stop.form.id)) return;
          const formKey = instanceKey(stop.form.id, stop.visitId);
          const saved = forms[formKey] || { values: {}, repeats: {} };
          forms[formKey] = {
            ...saved,
            values: applySharedFormDate(stop.form.fields, saved.values, saved.repeats, offer.date),
          };
        });
        return commitLinkedEdit(chartIdRef.current, 'alliance', { ...current, forms }, 'alliance.icf.date', offer.formKey);
      }
      const form = current.forms[offer.formKey] || { values: {}, repeats: {} };
      const stop = outlineStops(outline).find((item) => instanceKey(item.form.id, item.visitId) === offer.formKey);
      const edited = {
        ...current,
        forms: {
          ...current.forms,
          [offer.formKey]: {
            ...form,
            values: applyBaselineTestDate(stop?.form.fields, form.values, offer.date),
          },
        },
      };
      return commitLinkedEdit(chartIdRef.current, 'alliance', edited, '', offer.formKey);
    });
  };

  const dismissOfferedDate = () => {
    dateDeclinedRef.current[dateOffer?.kind || 'baseline'] = true;
    setDateOffer(null);
  };

  const updateRepeats = (key, groupId, count) => {
    setState((current) => {
      const form = current.forms[key] || { values: {}, repeats: {} };
      const previous = form.repeats?.[groupId] || 1;
      let nextValues = form.values || {};
      if (groupId === 'index_procedure' && count > previous) {
        for (let index = previous; index < count; index += 1) {
          nextValues = blankProcedureRow(nextValues, index);
        }
      }
      return {
        ...current,
        forms: {
          ...current.forms,
          [key]: { ...form, values: nextValues, repeats: { ...form.repeats, [groupId]: count } },
        },
      };
    });
  };

  if (error) return <div className="compass-error">{error}</div>;
  if (!fields || !location || (demo && !chartReady)) {
    return (
      <div className={`compass-workspace${embedded ? ' is-embedded' : ''}`}>
        {!embedded && (
          <header className="compass-top">
            <div>
              <h1 className="compass-title">Alliance</h1>
              <p className="compass-note">Example chart. Answers stay in this browser and are not written to the database.</p>
            </div>
          </header>
        )}
        <LoadingSpinner />
      </div>
    );
  }

  const { phase, visit, form } = location;
  const visitId = visit?.id || phase.visitId || '';
  const key = instanceKey(form.id, visitId);
  const storedValues = formValues(state, key);
  const values = VISIT_TIMEPOINT[visitId]
    ? { ...storedValues, 'alliance.visit.timepoint': VISIT_TIMEPOINT[visitId] }
    : storedValues;
  const markEligible = () => {
    const filled = eligibleEnrollmentValues(form.fields, localToday());
    setState((current) => {
      const saved = current.forms[key] || { values: {}, repeats: {} };
      const edited = {
        ...current,
        forms: {
          ...current.forms,
          [key]: { ...saved, values: { ...saved.values, ...filled } },
        },
      };
      return commitLinkedEdit(chartIdRef.current, 'alliance', edited, 'alliance.ie.all_met', key);
    });
  };
  const patientName = patient?.name || 'This patient';
  const storedAnswers = demoChartsUseDatabase()
    ? 'Answers are saved for this patient.'
    : 'Answers stay on this page and are not written to the database.';
  const note = demo
    ? `${patientName}. ${filledFromChart ? 'Filled from Phoenix. ' : ''}${handoff ? 'Joint Decisions values are filled in. ' : ''}${storedAnswers}`
    : 'Example chart. Answers stay in this browser and are not written to the database.';

  const percentFor = (itemForm, formVisitId) => {
    const saved = state.forms[instanceKey(itemForm.id, formVisitId)] || {};
    return formProgress(itemForm.fields, saved.values || {}, saved.repeats || {}).percent;
  };

  const groupDone = (groupKey) => {
    const [phaseId, visitGroupId] = groupKey.split(':');
    const phaseItem = outline.find((item) => item.id === phaseId);
    if (!phaseItem) return false;
    if (visitGroupId) {
      const itemVisit = phaseItem.visits?.find((item) => item.id === visitGroupId);
      return Boolean(itemVisit?.forms.every((itemForm) => percentFor(itemForm, itemVisit.id) === 100));
    }
    if (phaseItem.visits) {
      return phaseItem.visits.every((itemVisit) => (
        itemVisit.forms.every((itemForm) => percentFor(itemForm, itemVisit.id) === 100)
      ));
    }
    const formVisitId = phaseItem.visitId || '';
    return phaseItem.forms.every((itemForm) => percentFor(itemForm, formVisitId) === 100);
  };

  const staleGroups = Object.keys(revealed).filter((groupKey) => revealed[groupKey] && !groupDone(groupKey));
  if (staleGroups.length) {
    setRevealed((current) => {
      const next = { ...current };
      let changed = false;
      staleGroups.forEach((groupKey) => {
        if (next[groupKey]) {
          next[groupKey] = false;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }

  const toggleGroup = (groupKey) => {
    if (!groupDone(groupKey)) return;
    setRevealed((current) => ({ ...current, [groupKey]: !current[groupKey] }));
  };

  return (
    <div className={`compass-workspace${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
      <header className="compass-top">
        <div>
          <h1 className="compass-title">Alliance</h1>
          <p className="compass-note">{note}</p>
        </div>
        {!demo && (
          <div className="compass-controls" role="group" aria-label="Alliance chart">
            <button
              type="button"
              className="compass-text-button"
              onClick={() => {
                clearExampleChart();
                setState(emptyState());
              }}
            >
              Reset example
            </button>
          </div>
        )}
      </header>
      )}

      <div className="compass-shell">
        <nav className="compass-rail" aria-label="Alliance forms">
          {outline.map((item) => (
            <RailCategory
              key={item.id}
              label={item.label}
              complete={groupDone(item.id)}
              collapsed={groupDone(item.id) && !revealed[item.id]}
              onToggle={() => toggleGroup(item.id)}
            >
              {item.visits ? item.visits.map((itemVisit) => {
                const visitKey = `${item.id}:${itemVisit.id}`;
                const visitDone = groupDone(visitKey);
                return (
                  <RailCategory
                    key={itemVisit.id}
                    label={itemVisit.label}
                    visit
                    complete={visitDone}
                    collapsed={visitDone && !revealed[visitKey]}
                    onToggle={() => toggleGroup(visitKey)}
                  >
                    {itemVisit.forms.map((itemForm) => (
                      <RailButton
                        key={itemForm.id}
                        label={itemForm.title}
                        active={phase.id === item.id && visit?.id === itemVisit.id && form.id === itemForm.id}
                        percent={percentFor(itemForm, itemVisit.id)}
                        onClick={() => select(item, itemVisit, itemForm)}
                      />
                    ))}
                  </RailCategory>
                );
              }) : item.forms.map((itemForm) => (
                <RailButton
                  key={itemForm.id}
                  label={itemForm.title}
                  active={phase.id === item.id && form.id === itemForm.id && !visit}
                  percent={percentFor(itemForm, item.visitId || '')}
                  onClick={() => select(item, null, itemForm)}
                />
              ))}
            </RailCategory>
          ))}
        </nav>

        <main className="compass-main" onClick={onEngage}>
          <FormNavbar
            phase={phase.label}
            title={form.title}
            onWhatsNext={goToNextMissing}
            missingOn={following}
            missingLocked={locked}
            onToggleMissingLock={toggleLock}
          >
            {form.id === 'eligibility' && (
              <button type="button" className="compass-text-button" onClick={markEligible}>
                Mark as eligible
              </button>
            )}
          </FormNavbar>
          <CompassForm
            fields={form.fields}
            values={values}
            onChange={(fieldKey, value) => updateValue(key, fieldKey, value)}
            repeatState={state.forms[key]?.repeats || {}}
            onRepeatCount={(groupId, count) => updateRepeats(key, groupId, count)}
            idPrefix={key}
            highlightFieldKey={focusFieldKey}
            formTitle={form.title}
          />
        </main>
      </div>
      <Modal show={Boolean(dateOffer)} onHide={dismissOfferedDate} centered animation={false}>
        <Modal.Body>
          <p className="mb-0">
            {dateOffer?.kind === 'consent'
              ? `Use ${calendarDate(dateOffer.date)} for all consent fields?`
              : `Use ${dateOffer?.date} for all baseline tests?`}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={dismissOfferedDate}>No</Button>
          <Button variant="primary" onClick={applyOfferedDate}>Yes</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function RailCategory({ label, complete, collapsed, onToggle, visit = false, children }) {
  const headingClass = `compass-rail-heading${visit ? ' is-visit' : ''}${collapsed ? ' is-collapsed' : ''}`;
  return (
    <div className={`compass-rail-group${collapsed ? ' is-collapsed' : ''}${visit ? ' is-visit' : ''}`}>
      {complete ? (
        <button
          type="button"
          className={headingClass}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          title={label}
          onClick={onToggle}
        >
          <span className="compass-rail-label">{label}</span>
          <small>100%</small>
        </button>
      ) : (
        <div className={headingClass}>
          <span className="compass-rail-label">{label}</span>
        </div>
      )}
      <div className="compass-rail-children" aria-hidden={collapsed || undefined}>
        <div className="compass-rail-children-inner" inert={collapsed ? true : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}

function RailButton({ label, active, percent, onClick }) {
  return (
    <button
      type="button"
      className={`compass-nav-button compass-rail-button${active ? ' is-active' : ''}`}
      style={{ '--progress': `${percent}%` }}
      onClick={onClick}
    >
      <span className="compass-rail-label">{label}</span>
      <small>{percent}%</small>
    </button>
  );
}
